import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Phaser from 'phaser';
import { WorldMapScene } from '../game/scenes/WorldMapScene';
import { Destination } from '../App';
import { useAuth } from '../contexts/AuthContext';
import { ParametricSphereArt } from './ParametricSphereArt';
import { ResourceSearchPanel } from './ResourceSearchPanel';
import { findNearestResourceNode, ResourceNodeData } from '../utils/resourceNodeUtils';

import { getLoadingTip } from '../utils/loadingTips';

interface RiftStormData {
  id: string;
  x: number;
  y: number;
  radius: number;
  tier: string;
  color: string;
  expiresAt: number;
}

interface PirateRaidData {
  id: string;
  islandId: string;
  islandName: string;
  islandX: number;
  islandY: number;
  tier: string;
  expiresAt: number;
  piratesDefeated: number;
  totalPirates: number;
  rewardPool: number;
}

interface SeaMonsterData {
  id: string;
  type: string;
  x: number;
  y: number;
}

interface RiftScarData {
  id: string;
  x: number;
  y: number;
  name: string;
}

interface WorldMapProps {
  playerInfo: {
    name: string;
    character: string;
  };
  playerPosition: { x: number; y: number };
  onClose: () => void;
  destination: Destination | null;
  onSetDestination: (dest: Destination | null) => void;
  onReady?: () => void;
  riftStorms?: RiftStormData[];
  pirateRaids?: PirateRaidData[];
  seaMonsters?: SeaMonsterData[];
  riftScars?: RiftScarData[];
  resourceNodesList?: ResourceNodeData[];
}

export const WorldMap: React.FC<WorldMapProps> = ({ 
  playerInfo, 
  playerPosition, 
  onClose,
  destination,
  onSetDestination,
  onReady,
  riftStorms = [],
  pirateRaids = [],
  seaMonsters = [],
  riftScars = [],
  resourceNodesList = [],
}) => {
  const auth = useAuth();
  const isAdmin: boolean = auth?.accountInfo?.status === 'admin';
  const gameRef = useRef<Phaser.Game | null>(null);
  const [isMapLoading, setIsMapLoading] = useState<boolean>(true);
  const [loadingTipIndex, setLoadingTipIndex] = useState<number>(0);
  const sceneRef = useRef<WorldMapScene | null>(null);
  const didInitSceneRef = useRef<boolean>(false);
  const [pendingDestination, setPendingDestination] = useState<Destination | null>(destination);
  const [, setSceneReady] = useState<boolean>(false);
  const [currentZoom, setCurrentZoom] = useState<number>(1);
  const [zoomBounds, setZoomBounds] = useState<{ min: number; max: number }>({ min: 0.5, max: 2 });
  const [resourceSearchOpen, setResourceSearchOpen] = useState<boolean>(false);

  const currentLoadingTip: string = useMemo(() => getLoadingTip(loadingTipIndex), [loadingTipIndex]);

  // Handle zoom controls
  const handleZoomIn = useCallback(() => {
    if (!sceneRef.current) return;
    sceneRef.current.zoomIn();
    setCurrentZoom(sceneRef.current.getCurrentZoom());
  }, []);

  const handleZoomOut = useCallback(() => {
    if (!sceneRef.current) return;
    sceneRef.current.zoomOut();
    setCurrentZoom(sceneRef.current.getCurrentZoom());
  }, []);

  // Handle click on map to preview destination
  const handleSetDestination = useCallback((x: number, y: number, name?: string) => {
    const newDest = { x, y, name };
    setPendingDestination(newDest);
    // Also propagate to parent immediately so compass shows when map closes
    onSetDestination(newDest);
  }, [onSetDestination]);

  // Handle resource search selection
  const handleResourceSearch = useCallback((resourceType: string) => {
      if (!resourceNodesList || resourceNodesList.length === 0) {
        console.warn(`No resource nodes available for ${resourceType}`);
        return;
      }

      // Convert ResourceNodeData[] to a Map for compatibility with findNearestResourceNode
      const nodesMap = new Map<string, any>();
      resourceNodesList.forEach((node, index) => {
        nodesMap.set(`node_${index}`, {
          hitArea: {
            id: node.id,
            type: node.type,
            nodeName: node.name,
            x: node.x,
            y: node.y,
            harvestsRemaining: node.harvestsRemaining,
            maxHarvests: node.maxHarvests,
            harvestTime: node.harvestTime,
            requiredLevel: node.requiredLevel,
            requiredSkill: node.requiredSkill,
            isDepleted: node.harvestsRemaining <= 0,
          },
        });
      });

      // Find nearest node of requested type
      const nearestNode = findNearestResourceNode(
        nodesMap,
        playerPosition.x,
        playerPosition.y,
        resourceType,
      );

      if (nearestNode) {
        // Set destination to the resource node
        handleSetDestination(
          nearestNode.x,
          nearestNode.y,
          `${nearestNode.name} (${resourceType.replace(/_/g, ' ')})`,
        );
        // Close panel
        setResourceSearchOpen(false);
      } else {
        console.warn(`No available ${resourceType.replace(/_/g, ' ')} nodes found nearby`);
      }
    }, [resourceNodesList, playerPosition, handleSetDestination]);

  // Update zoom display periodically (for mouse wheel zoom)
  useEffect(() => {
    if (!sceneRef.current) return;
    
    const updateZoom = () => {
      if (sceneRef.current) {
        setCurrentZoom(sceneRef.current.getCurrentZoom());
      }
    };
    
    const interval = setInterval(updateZoom, 100);
    return () => clearInterval(interval);
  }, []);

  // Handle Escape key to close map
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Inject shimmer animation
  useEffect(() => {
    const styleId = 'world-map-shimmer-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes worldMapShimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    if (!isMapLoading) return;

    setLoadingTipIndex(0);
    const intervalId: number = window.setInterval(() => {
      setLoadingTipIndex((currentIndex: number) => currentIndex + 1);
    }, 4500);

    return () => window.clearInterval(intervalId);
  }, [isMapLoading]);

  useEffect(() => {
    setPendingDestination(destination);
  }, [destination]);

  useEffect(() => {
    // Initialize Phaser game for world map - make it square
    const availableWidth = window.innerWidth - 40;
    const availableHeight = window.innerHeight - 160;
    const mapSize = Math.min(availableWidth, availableHeight);
    
    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      width: mapSize,
      height: mapSize,
      parent: 'world-map-canvas',
      backgroundColor: '#0a1628',
      scene: [WorldMapScene],
      render: {
        antialias: false,
        pixelArt: true,
        roundPixels: true,
      },
    };

    gameRef.current = new Phaser.Game(config);

    const initScene = (): void => {
      if (didInitSceneRef.current) return;
      const scene = gameRef.current?.scene.getScene('WorldMapScene') as WorldMapScene | undefined;
      if (!scene) return;
      sceneRef.current = scene;
      didInitSceneRef.current = true;
      setSceneReady(true);

      scene.setPlayerPosition(playerPosition.x, playerPosition.y);
      scene.setDestinationCallback(handleSetDestination);
      if (pendingDestination) {
        scene.showDestinationMarker(pendingDestination.x, pendingDestination.y);
      }

      // Ensure current rift storm markers are applied even if the scene is initialized
      // after the latest React render (prevents missing storms until a change occurs).
      scene.updateRiftStorms(riftStorms);
      scene.updatePirateRaids(pirateRaids);

      // Initialize zoom state
      setCurrentZoom(scene.getCurrentZoom());
      setZoomBounds(scene.getZoomBounds());

      setIsMapLoading(false);
      if (onReady) {
        onReady();
      }
    };

    // Robust scene init: handle potential CREATE event race by polling briefly
    const tryInit = (): void => {
      try {
        initScene();
      } catch (error) {
        console.error('Failed to initialize WorldMapScene:', error);
      }
    };

    const scene = gameRef.current.scene.getScene('WorldMapScene') as WorldMapScene | undefined;
    if (scene && scene.sys.isActive()) {
      tryInit();
    } else if (scene) {
      scene.events.once(Phaser.Scenes.Events.CREATE, () => {
        tryInit();
      });
      scene.events.once(Phaser.Scenes.Events.START, () => {
        tryInit();
      });
    }

    const initPoll = window.setInterval(() => {
      if (didInitSceneRef.current) {
        window.clearInterval(initPoll);
        return;
      }
      tryInit();
    }, 50);

    const initTimeout = window.setTimeout(() => {
      window.clearInterval(initPoll);
      if (!didInitSceneRef.current) {
        console.error('World map scene init timed out; forcing loading UI to close');
        setIsMapLoading(false);
        if (onReady) {
          onReady();
        }
      }
    }, 3000);

    // Handle resize - keep map square
    const handleResize = (): void => {
      if (gameRef.current) {
        const newWidth = window.innerWidth - 40;
        const newHeight = window.innerHeight - 160;
        const newSize = Math.min(newWidth, newHeight);
        gameRef.current.scale.resize(newSize, newSize);
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.clearInterval(initPoll);
      window.clearTimeout(initTimeout);
      gameRef.current?.destroy(true);
      gameRef.current = null;
      sceneRef.current = null;
      didInitSceneRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    scene.setPlayerPosition(playerPosition.x, playerPosition.y);
  }, [playerPosition.x, playerPosition.y]);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    scene.setDestinationCallback(handleSetDestination);
  }, [handleSetDestination]);

  // Keep destination marker in sync after map has initialized
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    if (pendingDestination) {
      scene.showDestinationMarker(pendingDestination.x, pendingDestination.y);
    }
  }, [pendingDestination]);

  // Keep rift storm markers in sync
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    scene.updateRiftStorms(riftStorms);
  }, [riftStorms]);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    scene.updatePirateRaids(pirateRaids);
  }, [pirateRaids]);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    scene.updateSeaMonsters(seaMonsters);
  }, [seaMonsters]);

  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    scene.updateRiftScars(riftScars);
  }, [riftScars]);

  return (
    <>
      <div className="world-map-container">
        <header className="world-map-header">
        <div className="header-left">
          <h1 className="font-mono">WORLD MAP</h1>
        </div>
        <div className="header-center">
          <div className="zoom-controls">
            <button
              type="button"
              className="zoom-btn"
              onClick={handleZoomOut}
              disabled={currentZoom <= zoomBounds.min}
              title="Zoom out"
            >
              −
            </button>
            <span className="zoom-display">
              {Math.round(currentZoom * 100)}%
            </span>
            <button
              type="button"
              className="zoom-btn"
              onClick={handleZoomIn}
              disabled={currentZoom >= zoomBounds.max}
              title="Zoom in"
            >
              +
            </button>
          </div>
        </div>
        <div className="header-right">
          <button
            type="button"
            onClick={() => {
              if (!pendingDestination || !isAdmin) return;
              onSetDestination(pendingDestination);
              onClose();
            }}
            disabled={!pendingDestination || !isAdmin}
            style={{
              opacity: pendingDestination && isAdmin ? 1 : 0.5,
              cursor: pendingDestination && isAdmin ? 'pointer' : 'not-allowed',
              padding: '6px 12px',
              fontSize: '12px',
              marginRight: 4,
            }}
            title={isAdmin ? 'Send navigation target' : 'Admin only'}
          >
            {isAdmin ? 'GO HERE' : 'ADMIN ONLY'}
          </button>
          <button
            type="button"
            onClick={() => {
              setPendingDestination(null);
              onSetDestination(null);
            }}
            disabled={!pendingDestination}
            style={{
              opacity: pendingDestination ? 1 : 0.5,
              cursor: pendingDestination ? 'pointer' : 'not-allowed',
              padding: '6px 12px',
              fontSize: '12px',
              marginRight: 4,
            }}
          >
            CLEAR
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              background: '#dc2626',
              border: '1px solid #ef4444',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            CLOSE
          </button>
        </div>
      </header>

      <div className="world-map-canvas-wrapper">
        {isMapLoading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            zIndex: 10,
            pointerEvents: 'none',
          }}>
            {/* Background parametric sphere visualization */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: 'none',
            }}>
              <ParametricSphereArt
                width={window.innerWidth}
                height={window.innerHeight}
                dotSpacing={2.5}
                opacity={0.25}
              />
            </div>

            {/* Loading content */}
            <div style={{
              position: 'relative',
              zIndex: 11,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
            }}>
              <div style={{
                width: 32,
                height: 32,
                border: '3px solid #58a6ff33',
                borderTop: '3px solid #58a6ff',
                animation: 'worldMapShimmer 0.8s linear infinite',
              }} />
              <span style={{ color: '#8b949e', fontSize: '12px', fontFamily: 'var(--font-mono)' }}>Loading world map...</span>
              <span style={{ color: '#94a3b8', fontSize: '12px', maxWidth: 420, textAlign: 'center', fontFamily: 'var(--font-mono)' }}>
                {currentLoadingTip}
              </span>
            </div>
          </div>
        )}
        <div id="world-map-canvas" style={{ opacity: isMapLoading ? 0.3 : 1, transition: 'opacity 0.3s ease' }} />
      </div>

      {/* Resource Search Panel */}
      {resourceSearchOpen && (
        <ResourceSearchPanel
          isOpen={resourceSearchOpen}
          onClose={() => setResourceSearchOpen(false)}
          onSelectResourceType={handleResourceSearch}
        />
      )}

      <div className="world-map-legend">
        <div className="legend-section">
          <span className="legend-title">Locations</span>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#87ceeb' }} />
            <span>Trading Post [P] (Safe Zone)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#ffa500' }} />
            <span>Shipyard [S] (Safe Zone)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#3498db' }} />
            <span>Player Territory</span>
          </div>
        </div>
        <div className="legend-section">
          <span className="legend-title">Events</span>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#9b59b6' }} />
            <span>Rift Storm (Bonus Loot/XP)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#ff3333' }} />
            <span>Pirate Raid (Active Combat)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#1a3a5a' }} />
            <span>Sea Monster (Roaming)</span>
          </div>
        </div>
        <div className="legend-section">
          <span className="legend-title">Danger Level</span>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#2ecc71' }} />
            <span>Low (Sloops)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#f39c12' }} />
            <span>Medium (Caravels)</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#e74c3c' }} />
            <span>High (Brigantines+)</span>
          </div>
        </div>
      </div>

      <div className="world-map-controls">
        <span>Scroll to zoom • Drag to pan • Click to set destination</span>
        {pendingDestination && (
          <span className="current-destination">
            Destination: {pendingDestination.name || `(${Math.round(pendingDestination.x)}, ${Math.round(pendingDestination.y)})`}
          </span>
        )}
      </div>
      </div>
    </>
  );
};
